import { forwardRef, useEffect } from "react";
import { CountrySelector, defaultCountries, parseCountry, usePhoneInput } from "react-international-phone";
import { CaretDownIcon, InfoIcon } from "../../../assets/icons";
import styles from "./PhoneInput.module.scss";
import { africanCountries } from "../../utils/africanCountries";

const allowedCountries = defaultCountries.filter((country) => {
    const { iso2 } = parseCountry(country);
    return ["ng", "gh", "ke", "zm"].includes(iso2);
});

type Props = {
    label: string;
    value?: string;
    error?: string;
    warning?: string;
    required?: boolean;
    disabled?: boolean;
    disableCountrySelector?: boolean;
    inputSize?: "small" | "large" | "big";
    placeholder?: string;
    onChange?: (value: string) => void;
    onChangeParts?: (localNumber: string, dialCode: string) => void;
    onBlur?: () => void;
    showCountries?: boolean;
    showFlags?: boolean;
    showCountrySelector?: boolean;
    defaultCountry?: string;
    // Locks the country and allows every African country (for prefilled numbers).
    lockCountry?: boolean;
};

const PhoneInput = forwardRef<HTMLInputElement, Props>(({ label, value, error, warning, disabled, disableCountrySelector, inputSize = "small", placeholder = "Enter phone number", onChange, onChangeParts, onBlur, showFlags = true, showCountrySelector = true, defaultCountry = "ng", lockCountry = false }, ref) => {
    const countries = lockCountry ? africanCountries : allowedCountries;
    const countryLocked = disableCountrySelector || lockCountry;
    const phoneInput = usePhoneInput({
        defaultCountry,
        value,
        defaultMask: "... ... ....",
        disableDialCodeAndPrefix: true,
        countries,
        onChange: (data) => {
            const dialCode = `+${data.country.dialCode}`;
            const localNumber = data.inputValue.replace(/\s/g, '');
            onChange?.(data.phone.replace(/\s/g, ''));
            onChangeParts?.(`${dialCode}${localNumber}`, dialCode);
        },
    });

    // defaultCountry is read only on mount; follow a later one (e.g. branding) while nothing is typed.
    useEffect(() => {
        if (!phoneInput.inputValue && phoneInput.country.iso2 !== defaultCountry) phoneInput.setCountry(defaultCountry);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultCountry]);

    return (
        <div className={styles.wrapper}>
            {label != "" &&
            <label className={styles.label}>
                {label}
                {/* {required && <span className={styles.required}>*</span>} */}
            </label>}
            <div className={`${styles.inputErrorContainer} ${label === "" ? styles.largeErrorContainer : ""} ${inputSize === "large" ? styles.inputErrorContainerLarge : ""}`}>

                <div className={`${styles.inputContainer} ${inputSize === "big" ? styles.sizeBig : ""} ${error ? styles.errorBorder : ""} ${warning && !error ? styles.warningBackground : ""}`}>
                    {showCountrySelector ? (
                        <CountrySelector
                            selectedCountry={phoneInput.country.iso2}
                            onSelect={(country) => phoneInput.setCountry(country.iso2)}
                            countries={countries}
                            disabled={disabled || countryLocked}
                            dropdownArrowStyle={{ display: "none" }}
                            flagStyle={{ display: showFlags ? "inline" : "none" }}
                            renderButtonWrapper={({ children, rootProps }) => (
                                <button {...rootProps} type="button" className={`${styles.flagButton} ${inputSize === "big" ? styles.sizeBig : ""}`} style={{ cursor: countryLocked ? "default" : undefined }}>
                                    {children}
                                    <span className={styles.dialCode}>+{phoneInput.country.dialCode}</span>
                                    {!countryLocked && <CaretDownIcon className={styles.caret} />}
                                </button>
                            )}
                        />
                    ) : (
                        <span className={`${styles.staticDialCode} ${styles.dialCode} ${inputSize === "big" ? styles.sizeBig : ""}`}>+{phoneInput.country.dialCode}</span>
                    )}
                    <input
                        ref={ref}
                        className={styles.input}
                        value={phoneInput.inputValue}
                        type="tel"
                        placeholder={placeholder}
                        disabled={disabled}
                        onChange={phoneInput.handlePhoneValueChange}
                        onBlur={onBlur}
                    />
                </div>
                {error && <span className={styles.error}>{error}</span>}
                {warning && !error && (
                    <span className={styles.warning}>
                        <InfoIcon className={styles.warningIcon} />
                        {warning}
                    </span>
                )}
            </div>
        </div>
    );
});

export default PhoneInput;
